import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePermisosPortalBancos } from "@/contexts/BankImpersonationContext";
import { esRpcInexistente, retrySalvoSinPermiso } from "@/lib/rpcErrors";
import { bancosRechazadosKey } from "@/hooks/usePortalBancos/useBancosDisponibles";
import {
  idsRolesBroker, resolverRolesBroker, puedeDerivarBroker, PERMISO_DERIVAR_A_BANCO, type RolesBroker,
} from "@/lib/portal-bancos/broker";
import {
  normalizarHistorialFormaPago, rutaEvidenciaFormaPago,
  type CambioFormaPagoHistorial, type DestinoFormaPago, type EvidenciaFormaPago, type ResultadoCambioFormaPago,
} from "@/lib/portal-bancos/cambioFormaPago";
import { sanitizeFilename } from "@/lib/storage-keys";

/**
 * Broker Bancario (ticket #1226): desde oct-2026 las solicitudes de crédito de
 * los clientes llegan a una figura central que las deriva a UN banco a la vez.
 * Vive como submenú "Broker" del Portal Bancos y se gobierna por permisos
 * (`submenus_permisos`), como el resto del portal.
 *
 * BD (migraciones 20261005193417-20261005193509): `bancos_solicitudes.canal`,
 * RLS del broker sobre el canal 'broker', RPCs `broker_derivar_solicitud` y
 * `broker_clientes_sin_forma_pago`.
 */

/** Ruta del submenú; su permiso es lo que hace "broker" a un rol. */
export const BROKER_PATH = "/admin/portal-bancos/clientes";

/**
 * Permisos del usuario sobre el submenú Broker (Super Admin: todos), sin escritura
 * mientras se impersona ("Ver como", solo lectura).
 */
export function usePermisosBroker() {
  return usePermisosPortalBancos(BROKER_PATH);
}

/**
 * ¿El usuario puede derivar solicitudes del broker a un banco? Permiso `derivar_a_banco` del
 * submenú del broker, que se da en Roles y Permisos. Si el ambiente aún no lo tiene en el
 * catálogo, cae al `actualizar` que la RPC pedía antes (ver `puedeDerivarBroker`).
 *
 * El broker dado de baja también deriva: su lista ya trae solo los clientes que atendió y las RPC
 * rechazan cualquier otra cuenta (ticket #1498).
 */
export function usePuedeDerivarBroker(idBanco: number | null | undefined): boolean {
  const { canDeriveToBank, canUpdate } = usePermisosBroker();
  const { data: permisoDerivarExiste = null } = useQuery({
    queryKey: ["permiso-existe", PERMISO_DERIVAR_A_BANCO],
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase
        .from("permisos")
        .select("id")
        .eq("nombre", PERMISO_DERIVAR_A_BANCO)
        .eq("activo", true)
        .limit(1);
      if (error) throw error;
      return (data ?? []).length > 0;
    },
  });
  return puedeDerivarBroker({ idBanco, puedeDerivar: canDeriveToBank, puedeActualizar: canUpdate, permisoDerivarExiste });
}

/**
 * Ids de los dos roles del broker, resueltos por nombre (los ids cambian entre
 * ambientes): el operador ("Broker Bancario") y el supervisor ("Supervisor
 * Broker", ticket #1245). `supervisorRolId` null = el ambiente aún no tiene la
 * migración del supervisor y todo sigue como antes.
 */
export function useRolesBroker() {
  return useQuery({
    queryKey: ["broker-roles"],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<RolesBroker> => {
      const { data, error } = await supabase
        .from("roles")
        .select("id, nombre")
        .eq("activo", true);
      if (error) throw error;
      return resolverRolesBroker((data ?? []) as { id: number; nombre: string | null }[]);
    },
  });
}

export interface EjecutivoBroker {
  email: string;
  nombre: string;
}

/**
 * Usuarios activos del broker (a quién se asigna un caso): operadores y
 * supervisores, que también llevan casos.
 */
export function useEquipoBroker(enabled = true) {
  const { data: roles } = useRolesBroker();
  const rolIds = idsRolesBroker(roles);
  return useQuery({
    queryKey: ["broker-equipo", rolIds.join("-") || "none"],
    enabled: enabled && rolIds.length > 0,
    staleTime: 60_000,
    queryFn: async (): Promise<EjecutivoBroker[]> => {
      const { data, error } = await (supabase as any)
        .from("usuarios")
        .select("email, nombre")
        .in("rol_id", rolIds)
        .eq("activo", true)
        .order("nombre", { ascending: true });
      if (error || !data) return [];
      return (data as any[]).map((u) => ({ email: u.email, nombre: u.nombre ?? u.email }));
    },
  });
}

export interface ResultadoDerivacion {
  id_solicitud: number;
  id_banco: number;
  banco: string;
  /** true = se creó una solicitud nueva (la anterior estaba cerrada). */
  nueva: boolean;
}

/**
 * Deriva una solicitud del broker a un banco con convenio. La regla (un banco a
 * la vez, BR001, consentimiento) vive en la RPC; aquí solo se invoca y se
 * refrescan las vistas.
 */
export function useDerivarSolicitud() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      idSolicitud: number;
      idBanco: number;
      nota?: string;
    }): Promise<ResultadoDerivacion> => {
      const { data, error } = await (supabase as any).rpc("broker_derivar_solicitud", {
        p_id_solicitud: input.idSolicitud,
        p_id_banco: input.idBanco,
        p_nota: input.nota?.trim() || null,
      });
      if (error) throw error;
      return data as ResultadoDerivacion;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["solicitudes-banco"] });
      qc.invalidateQueries({ queryKey: ["banco-solicitud-notas"] });
      qc.invalidateQueries({ queryKey: bancosRechazadosKey().slice(0, 1) });
    },
  });
}

/** Solicitud que arma el broker: sin banco (en seguimiento) no trae banco. */
export interface ResultadoCrearSolicitud {
  id_solicitud: number;
  id_banco: number | null;
  banco: string | null;
  nueva: boolean;
}

/**
 * El broker arma la solicitud de quien eligió crédito y aún no la manda desde la app, y la deriva
 * a un banco en el mismo paso (RPC `broker_crear_solicitud_y_derivar`). Exige la autorización
 * del cliente para compartir sus datos y cómo la dio: queda en la Bitácora. Sin banco (ticket
 * #1415) la solicitud queda en Contactado, sin derivar: es lo que acordó con el cliente y lo que
 * él ve en su app.
 */
export function useCrearSolicitudYDerivar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      idCuenta: number;
      idBanco: number | null;
      monto: number;
      plazoAnios: number;
      nota?: string;
      medio: string;
    }): Promise<ResultadoCrearSolicitud> => {
      const { data, error } = await (supabase as any).rpc("broker_crear_solicitud_y_derivar", {
        p_id_cuenta_cobranza: input.idCuenta,
        p_id_banco: input.idBanco,
        p_monto_financiar: input.monto,
        p_plazo_anios: input.plazoAnios,
        p_nota: input.nota?.trim() || null,
        p_consentimiento_confirmado: true,
        p_medio_consentimiento: input.medio,
      });
      if (error) throw error;
      return data as ResultadoCrearSolicitud;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["solicitudes-banco"] });
      qc.invalidateQueries({ queryKey: CLIENTES_KEY });
      qc.invalidateQueries({ queryKey: ["broker-ficha-cliente"] });
      qc.invalidateQueries({ queryKey: bancosRechazadosKey().slice(0, 1) });
    },
  });
}

/** Fila de `broker_clientes_sin_forma_pago`. */
export interface ClienteSinFormaPago {
  id_cuenta_cobranza: number;
  id_propiedad: number;
  id_proyecto: number;
  proyecto: string;
  unidad: string;
  id_persona: number;
  cliente: string;
  email: string | null;
  clave_pais_telefono: string | null;
  telefono: string | null;
  fecha_compra: string | null;
  precio_final: number;
  pagado: number;
  saldo: number;
  acuerdos_pendientes: number;
  pago_final_habilitado: boolean;
  /** La app ya le muestra la elección de forma de pago final. */
  pago_final_disponible: boolean;
  /** El cliente ya tiene usuario para entrar a la app. */
  tiene_usuario_app: boolean;
  /** Lada (solo dígitos) del país del teléfono, resuelta en `paises`. */
  lada: string | null;
  /** 'pf' / 'pm'. Opcionales: llegan con la migración 20261007052317. */
  tipo_persona?: string | null;
  /** La cuenta tiene más de un comprador activo. */
  copropiedad?: boolean;
  /** Nombres de los otros compradores. */
  copropietarios?: string | null;
  /** Contacto del CRM del cliente, donde viven sus notas, tareas y citas. */
  id_entidad_crm?: number | null;
  /**
   * `cuentas_cobranza.tipo_financiamiento`: null = aún no elige; en la lista de
   * clientes por contactar también llega 'FINANCIAMIENTO_EXTERNO' (eligió otro
   * banco u otro arreglo y pidió que lo contacten, ticket #1245) y, desde
   * 20261008071953, 'CREDITO_HIPOTECARIO' (eligió crédito y le falta su solicitud).
   * Opcional: sin la migración de la lista no viene y cuenta como no elegido.
   */
  tipo_financiamiento?: string | null;
  /**
   * Dueño de la unidad (la entidad con la que se dio de alta: Tallwood, Corporativo
   * Jmdq, …), nombre comercial o legal; null = sin dueño. Opcionales: llegan con la
   * migración 20261008163417 (ticket #1245).
   */
  dueno?: string | null;
  id_persona_dueno?: number | null;
}

/**
 * Filas de las RPCs de clientes del broker con números parseados y la lada del
 * teléfono: `personas.clave_pais_telefono` es el país ("MX"); la lada vive en `paises`.
 */
async function normalizarClientes(rows: any[]): Promise<ClienteSinFormaPago[]> {
  const paisIds = Array.from(new Set(rows.map((r) => r.clave_pais_telefono).filter(Boolean)));
  const { data: paises } = paisIds.length
    ? await (supabase as any).from("paises").select("id, clave_pais_telefono").in("id", paisIds)
    : { data: [] };
  const ladaPorPais = new Map<string, string>(
    ((paises as any[]) ?? []).map((p) => [String(p.id), String(p.clave_pais_telefono ?? "")]),
  );
  return rows.map((r) => ({
    ...r,
    precio_final: Number(r.precio_final) || 0,
    pagado: Number(r.pagado) || 0,
    saldo: Number(r.saldo) || 0,
    acuerdos_pendientes: Number(r.acuerdos_pendientes) || 0,
    // Sin la columna (RPC anterior a la migración) = aún no elige.
    tipo_financiamiento: r.tipo_financiamiento ?? null,
    // Sin la columna (RPC anterior a 20261008163417) = sin dueño.
    dueno: r.dueno ?? null,
    id_persona_dueno: r.id_persona_dueno != null ? Number(r.id_persona_dueno) : null,
    lada:r.clave_pais_telefono ? ladaPorPais.get(String(r.clave_pais_telefono)) ?? null : null,
  })) as ClienteSinFormaPago[];
}

const CLIENTES_KEY = ["broker-clientes-sin-forma-pago"] as const;

async function fetchClientesSinFormaPago(): Promise<{ filas: ClienteSinFormaPago[]; pendiente: boolean }> {
  const { data, error } = await (supabase as any).rpc("broker_clientes_sin_forma_pago", {
    p_id_proyecto: null,
  });
  if (error) {
    if (esRpcInexistente(error)) return { filas: [], pendiente: true };
    throw error;
  }
  return { filas: await normalizarClientes((data as any[]) ?? []), pendiente: false };
}

/**
 * Clientes que aún no eligen cómo liquidar o que eligieron financiamiento externo,
 * en proyectos que aceptan crédito hipotecario, para que el broker los contacte.
 * `true` en `pendiente` = la RPC no existe todavía en este ambiente (front
 * adelante del DDL).
 */
export function useClientesSinFormaPago(enabled = true) {
  return useQuery({
    queryKey: CLIENTES_KEY,
    enabled,
    staleTime: 60_000,
    retry: retrySalvoSinPermiso,
    queryFn: fetchClientesSinFormaPago,
  });
}

/**
 * Fila de `broker_ficha_cliente`: la misma de la lista, con su forma de pago final
 * (`tipo_financiamiento`) sea cual sea.
 */
export type FichaClienteBroker = ClienteSinFormaPago;

/**
 * Ficha de un cliente del broker por su cuenta de cobranza (también si ya tiene
 * solicitud o está liquidado). null = no existe o no es de lo que maneja el broker.
 *
 * Front adelante del DDL: sin `broker_ficha_cliente` en el ambiente se toma de la
 * lista de clientes por contactar, que solo trae a quien aún no elige forma de pago
 * o eligió financiamiento externo.
 */
export function useFichaClienteBroker(idCuenta: number | null | undefined) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ["broker-ficha-cliente", idCuenta ?? null],
    enabled: idCuenta != null,
    staleTime: 30_000,
    retry: retrySalvoSinPermiso,
    queryFn: async (): Promise<FichaClienteBroker | null> => {
      const { data, error } = await (supabase as any).rpc("broker_ficha_cliente", {
        p_id_cuenta_cobranza: idCuenta,
      });
      if (error) {
        if (!esRpcInexistente(error)) throw error;
        const lista = await qc.fetchQuery({ queryKey: CLIENTES_KEY, queryFn: fetchClientesSinFormaPago, staleTime: 60_000 });
        return lista.filas.find((f) => f.id_cuenta_cobranza === idCuenta) ?? null;
      }
      const [fila] = await normalizarClientes((data as any[]) ?? []);
      return fila ?? null;
    },
  });
}

/** Comprador de la cuenta para la ficha del broker. */
export interface CompradorCuentaBroker {
  idPersona: number;
  nombre: string;
  /** `compradores.porcentaje_copropiedad`; 0 si no está capturado. */
  porcentaje: number;
}

/**
 * Compradores activos de la cuenta, el titular primero. Sin solicitud la ficha solo traía
 * los NOMBRES de los copropietarios: con esto la pestaña Cliente deja ver los datos y
 * documentos de cada uno (pedido de la Supervisora Broker, oct-2026). Waterfall de dos
 * lecturas: `compradores` y `personas` ya se leen así en el resto del panel.
 */
export function useCompradoresCuentaBroker(idCuenta: number | null | undefined, idTitular: number | null | undefined) {
  return useQuery({
    queryKey: ["broker-compradores-cuenta", idCuenta ?? null],
    enabled: idCuenta != null,
    staleTime: 60_000,
    queryFn: async (): Promise<CompradorCuentaBroker[]> => {
      const sb = supabase as any;
      const { data: filas, error } = await sb
        .from("compradores")
        .select("id_persona, porcentaje_copropiedad")
        .eq("id_cuenta_cobranza", idCuenta)
        .eq("activo", true);
      if (error) throw error;
      const ids = [...new Set(((filas as any[]) ?? []).map((c) => Number(c.id_persona)).filter(Boolean))];
      if (ids.length === 0) return [];
      const { data: personas, error: ePer } = await sb.from("personas").select("id, nombre_legal").in("id", ids);
      if (ePer) throw ePer;
      const nombre = new Map<number, string>(((personas as any[]) ?? []).map((p) => [Number(p.id), String(p.nombre_legal ?? "").trim()]));
      return ((filas as any[]) ?? [])
        .map((c) => ({
          idPersona: Number(c.id_persona),
          nombre: nombre.get(Number(c.id_persona)) || "Sin nombre",
          porcentaje: Number(c.porcentaje_copropiedad) || 0,
        }))
        .sort((a, b) =>
          Number(b.idPersona === idTitular) - Number(a.idPersona === idTitular) || a.nombre.localeCompare(b.nombre));
    },
  });
}

/**
 * El Supervisor Broker (o Super Admin) cambia la forma de pago final del cliente
 * (ticket #1245): RPC `broker_cambiar_forma_pago` (migración 20261008051847). La regla
 * vive en la RPC: quién puede, qué cuentas, qué pasa con la solicitud y el crédito, y el
 * rastro en la Bitácora y en logs_actividad. `tipo` null = "aún no elige".
 *
 * Refresca la ficha, Clientes por contactar, las solicitudes (Bandeja, Proceso, la vista
 * del broker), su Bitácora y la solicitud vigente que ven Cobranza y el portal del cliente.
 */
export function useCambiarFormaPagoBroker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      idCuenta: number;
      tipo: DestinoFormaPago;
      motivo: string;
      /** Foto o PDF opcional; se sube antes de llamar a la RPC. */
      evidencia?: File[];
    }): Promise<ResultadoCambioFormaPago> => {
      const archivos = input.evidencia ?? [];
      const subidas: EvidenciaFormaPago[] = [];
      for (const f of archivos) {
        const uuid = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const path = rutaEvidenciaFormaPago(input.idCuenta, sanitizeFilename(f.name), uuid);
        const { error: upErr } = await supabase.storage
          .from(BUCKET_EVIDENCIA_FORMA_PAGO)
          .upload(path, f, { contentType: f.type || undefined, upsert: false });
        if (upErr) throw upErr;
        subidas.push({ path, nombre: f.name, mime: f.type || null });
      }
      const sb = supabase as any;
      const { data, error } = await sb.rpc("broker_cambiar_forma_pago_con_evidencia", {
        p_id_cuenta_cobranza: input.idCuenta,
        p_tipo: input.tipo,
        p_motivo: input.motivo,
        p_evidencia: subidas,
      });
      // Sin la migración 20261013214713: si no hay evidencia, la RPC de siempre basta.
      if (error && esRpcInexistente(error) && subidas.length === 0) {
        const r = await sb.rpc("broker_cambiar_forma_pago", {
          p_id_cuenta_cobranza: input.idCuenta,
          p_tipo: input.tipo,
          p_motivo: input.motivo,
        });
        if (r.error) throw r.error;
        return r.data as ResultadoCambioFormaPago;
      }
      if (error) throw error;
      return data as ResultadoCambioFormaPago;
    },
    onSuccess: (_r, input) => {
      qc.invalidateQueries({ queryKey: ["broker-ficha-cliente", input.idCuenta] });
      qc.invalidateQueries({ queryKey: CLIENTES_KEY });
      qc.invalidateQueries({ queryKey: ["solicitudes-banco"] });
      qc.invalidateQueries({ queryKey: ["banco-solicitud-notas"] });
      qc.invalidateQueries({ queryKey: ["solicitud-credito-vigente", input.idCuenta] });
      qc.invalidateQueries({ queryKey: ["broker-historial-forma-pago", input.idCuenta] });
    },
  });
}

/** Bucket de la evidencia (el mismo del chat del broker). */
export const BUCKET_EVIDENCIA_FORMA_PAGO = "documentos";

/**
 * Historial de cambios de forma de pago de la cuenta (motivo, quién, cuándo y evidencia),
 * para la bitácora de la ficha. Sin la RPC (migración pendiente) devuelve [].
 */
export function useHistorialFormaPago(idCuenta: number | null) {
  return useQuery({
    queryKey: ["broker-historial-forma-pago", idCuenta],
    enabled: idCuenta != null,
    retry: retrySalvoSinPermiso,
    queryFn: async (): Promise<CambioFormaPagoHistorial[]> => {
      const { data, error } = await (supabase as any).rpc("broker_historial_forma_pago", {
        p_id_cuenta_cobranza: idCuenta,
      });
      if (error) {
        if (esRpcInexistente(error)) return [];
        throw error;
      }
      return normalizarHistorialFormaPago(data);
    },
  });
}

/** URL firmada (1 h) de un archivo de evidencia. */
export async function urlEvidenciaFormaPago(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET_EVIDENCIA_FORMA_PAGO).createSignedUrl(path, 3600);
  if (error || !data?.signedUrl) throw error ?? new Error("Sin URL");
  return data.signedUrl;
}
